import { aCentimos, formatear, type Moneda } from '@mc/comercio'
import { type Campo, importeDelPago } from '@mc/formularios'
import { useQuery } from '@tanstack/react-query'
import {
  Calculator,
  ChevronDown,
  CreditCard,
  EyeOff,
  Plus,
  Star,
  Trash2,
  Upload,
} from 'lucide-react'
import { useState, type ReactNode } from 'react'

import { consulta, pedir } from '../api/nucleo'
import { clases } from '../componentes/ui'

/**
 * Un campo de formulario **como lo verá quien lo rellene**, con un estilo limpio y neutro y
 * el color de marca de la web (la variable `--marca`, que pone quien lo envuelve). Lo usan
 * el lienzo del constructor (`muestra`: los controles están, pero no se tocan) y la vista
 * previa (`vivo`: se rellenan, con su error junto al campo).
 *
 * Un formulario es contenido, no diseño (§9): aquí no hay colores ni tipografías por campo.
 * Lo único que cambia la forma es el ancho, y eso lo pone la rejilla de quien lo envuelve.
 *
 * Un tipo que el panel no conozca (el núcleo puede añadir tipos sin tocar el panel) sale
 * como un campo de texto con su etiqueta, nunca roto.
 */
export type CampoDePrevia = Campo & Record<string, unknown>

type Props = {
  campo: CampoDePrevia
  modo: 'muestra' | 'vivo'
  /** Prefijo de los `id` del DOM: dos vistas del mismo formulario no pueden compartirlos. */
  id: string
  valor?: unknown
  cambiar?: (valor: unknown) => void
  error?: string
  /** Todos los errores, por clave: los de las filas de un repetidor van como `personas.0.nombre`. */
  errores?: Record<string, string>
  /** Los valores con los calculados ya puestos: lo que enseñan un total y un pago. */
  totales?: Record<string, unknown>
  moneda: Moneda
  sitio: number
  /** La etiqueta de otro campo por su clave («el total»), para decir de dónde sale un importe. */
  etiquetaDe?: (clave: string) => string
}

const texto = (v: unknown): string => (v === null || v === undefined ? '' : String(v))
const numero = (v: unknown): null | number =>
  typeof v === 'number' && Number.isFinite(v) ? v : null

export const claseDeEntrada = (error?: string) =>
  clases(
    'block w-full rounded-lg border bg-papel px-3 py-2.5 text-[15px] text-tinta placeholder:text-tenue transition-colors focus:border-(--marca) focus:ring-3 focus:ring-(--marca)/20 focus:outline-none',
    error ? 'border-error' : 'border-linea-fuerte',
  )

const Asterisco = ({ obligatorio }: { obligatorio?: boolean | null }) =>
  obligatorio ? (
    <>
      <span aria-hidden className="ml-0.5 text-error">
        *
      </span>
      <span className="sr-only"> (obligatorio)</span>
    </>
  ) : null

const Rotulo = ({ campo, htmlFor }: { campo: CampoDePrevia; htmlFor?: string }) => (
  <label className="mb-1.5 block text-sm font-medium text-tinta" htmlFor={htmlFor}>
    {campo.etiqueta || <span className="text-tenue">Sin etiqueta</span>}
    <Asterisco obligatorio={campo.obligatorio} />
  </label>
)

const Pie = ({
  campo,
  idAyuda,
  idError,
  error,
}: {
  campo: CampoDePrevia
  error?: string
  idAyuda: string
  idError: string
}) => (
  <>
    {campo.ayuda && (
      <p className="mt-1.5 text-[13px] text-tenue" id={idAyuda}>
        {campo.ayuda}
      </p>
    )}
    {error && (
      <p className="mt-1.5 text-[13px] font-medium text-error" id={idError} role="alert">
        {error}
      </p>
    )}
  </>
)

/** Un grupo de controles (opciones, estrellas, la escala): la etiqueta es la leyenda. */
const Grupo = ({
  campo,
  children,
  describe,
}: {
  campo: CampoDePrevia
  children: ReactNode
  describe?: string
}) => (
  <fieldset aria-describedby={describe}>
    <legend className="mb-2 text-sm font-medium text-tinta">
      {campo.etiqueta || <span className="text-tenue">Sin etiqueta</span>}
      <Asterisco obligatorio={campo.obligatorio} />
    </legend>
    {children}
  </fieldset>
)

const TIPO_DE_ENTRADA: Record<string, string> = {
  texto: 'text',
  email: 'email',
  telefono: 'tel',
  url: 'url',
  numero: 'number',
  fecha: 'date',
  hora: 'time',
}

const MARCADOR_POR_OMISION: Record<string, string> = {
  email: 'nombre@ejemplo.com',
  telefono: '+51 999 999 999',
  url: 'https://',
}

export const CampoDelFormulario = (p: Props) => {
  const { campo, modo, error } = p
  const vivo = modo === 'vivo'
  const id = `${p.id}-${campo.clave || 'campo'}`
  const idAyuda = `${id}-ayuda`
  const idError = `${id}-error`
  const describe =
    [campo.ayuda ? idAyuda : '', error ? idError : ''].filter(Boolean).join(' ') || undefined
  const comunes = {
    id,
    'aria-describedby': describe,
    'aria-invalid': error ? true : undefined,
    // En el lienzo los controles están, pero no se tocan (los envuelve un `inert`).
    ...(vivo ? {} : { readOnly: true, tabIndex: -1 }),
  }
  const marcador = texto(campo.marcador) || MARCADOR_POR_OMISION[campo.tipo] || undefined
  const pie = <Pie campo={campo} error={error} idAyuda={idAyuda} idError={idError} />

  // El tipo es un texto: el núcleo puede mandar tipos que el paquete todavía no nombra (url, lugar, escala).
  switch (campo.tipo as string) {
    case 'oculto':
      return vivo ? null : (
        <div className="flex items-center gap-2 rounded-lg border border-dashed border-linea-fuerte px-3 py-2 text-[13px] text-tenue">
          <EyeOff aria-hidden className="size-4 shrink-0" />
          <span className="min-w-0 truncate">
            <span className="font-medium text-tinta-2">{campo.etiqueta || campo.clave}</span> ·
            campo oculto: no lo ve quien rellena, lo pone el sistema al enviar.
          </span>
        </div>
      )

    case 'informativo':
      return texto(campo.texto) ? (
        <div className="rounded-lg bg-papel-2 px-4 py-3 text-sm whitespace-pre-line text-tinta-2">
          {texto(campo.texto)}
        </div>
      ) : (
        <div className="rounded-lg border border-dashed border-linea-fuerte px-4 py-3 text-sm text-tenue">
          Texto informativo: escríbelo en los ajustes del campo.
        </div>
      )

    case 'area':
    case 'direccion':
      return (
        <div>
          <Rotulo campo={campo} htmlFor={id} />
          <textarea
            {...comunes}
            className={clases(claseDeEntrada(error), 'min-h-24 resize-y')}
            onChange={(e) => p.cambiar?.(e.target.value)}
            placeholder={marcador}
            rows={campo.tipo === 'direccion' ? 2 : 4}
            value={vivo ? texto(p.valor) : ''}
          />
          {pie}
        </div>
      )

    case 'desplegable': {
      const opciones = campo.opciones ?? []
      return (
        <div>
          <Rotulo campo={campo} htmlFor={id} />
          <span className="relative block">
            <select
              {...comunes}
              className={clases(claseDeEntrada(error), 'appearance-none pr-9')}
              onChange={(e) => p.cambiar?.(e.target.value)}
              value={vivo ? texto(p.valor) : ''}
            >
              <option value="">{marcador ?? 'Elige una opción'}</option>
              {opciones.map((o) => (
                <option key={o.valor} value={o.valor}>
                  {o.etiqueta}
                </option>
              ))}
            </select>
            <ChevronDown
              aria-hidden
              className="pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2 text-tenue"
            />
          </span>
          {pie}
        </div>
      )
    }

    case 'opcion-multiple':
    case 'casillas': {
      const multiple = campo.tipo === 'casillas'
      const elegidas = Array.isArray(p.valor)
        ? p.valor.map(String)
        : texto(p.valor)
          ? [texto(p.valor)]
          : []
      const opciones = campo.opciones ?? []
      return (
        <div>
          <Grupo campo={campo} describe={describe}>
            <div className={clases('grid gap-2', opciones.length > 4 && '@md:grid-cols-2')}>
              {opciones.map((o) => (
                <label
                  className="flex cursor-pointer items-center gap-2.5 rounded-lg border border-linea px-3 py-2.5 text-[15px] text-tinta has-checked:border-(--marca) has-checked:bg-(--marca)/5"
                  key={o.valor}
                >
                  <input
                    checked={vivo ? elegidas.includes(o.valor) : false}
                    className="size-4 shrink-0 accent-(--marca)"
                    name={id}
                    onChange={(e) => {
                      if (!multiple) p.cambiar?.(o.valor)
                      else
                        p.cambiar?.(
                          e.target.checked
                            ? [...elegidas, o.valor]
                            : elegidas.filter((v) => v !== o.valor),
                        )
                    }}
                    readOnly={!vivo}
                    tabIndex={vivo ? undefined : -1}
                    type={multiple ? 'checkbox' : 'radio'}
                    value={o.valor}
                  />
                  {o.etiqueta || <span className="text-tenue">Opción sin texto</span>}
                </label>
              ))}
              {opciones.length === 0 && (
                <p className="text-sm text-tenue">
                  Sin opciones todavía: añádelas en los ajustes del campo.
                </p>
              )}
            </div>
          </Grupo>
          {pie}
        </div>
      )
    }

    case 'consentimiento': {
      const aceptado = p.valor === true
      return (
        <div>
          {campo.etiqueta && (
            <p className="mb-1.5 text-sm font-medium text-tinta">{campo.etiqueta}</p>
          )}
          <label
            className="flex cursor-pointer items-start gap-2.5 text-sm text-tinta-2"
            htmlFor={id}
          >
            <input
              {...comunes}
              checked={vivo ? aceptado : false}
              className="mt-0.5 size-4 shrink-0 accent-(--marca)"
              onChange={(e) => p.cambiar?.(e.target.checked)}
              type="checkbox"
            />
            <span>
              {texto(campo.textoDelConsentimiento) || (
                <span className="text-tenue">
                  Escribe en los ajustes del campo lo que acepta quien rellena.
                </span>
              )}
              <Asterisco obligatorio={campo.obligatorio} />
            </span>
          </label>
          {pie}
        </div>
      )
    }

    case 'archivo': {
      const extensiones = Array.isArray(campo.extensiones) ? campo.extensiones : []
      const maximo = numero(campo.maximoDeArchivos) ?? 1
      const nombres = Array.isArray(p.valor) ? p.valor.map(String) : []
      return (
        <div>
          <Rotulo campo={campo} htmlFor={id} />
          <label
            className={clases(
              'flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded-lg border-2 border-dashed bg-papel-2/60 px-4 py-6 text-center text-sm text-tinta-2 transition-colors hover:border-(--marca)',
              error ? 'border-error' : 'border-linea-fuerte',
            )}
            htmlFor={id}
          >
            <span className="flex size-9 items-center justify-center rounded-full bg-papel text-(--marca) shadow-suave">
              <Upload aria-hidden className="size-4" />
            </span>
            <span>
              Arrastra aquí {maximo === 1 ? 'el archivo' : 'los archivos'} o{' '}
              <span className="font-medium text-(--marca) underline underline-offset-2">
                elígelos
              </span>
            </span>
            <span className="text-xs text-tenue">
              {extensiones.length > 0
                ? extensiones.map((e) => `.${e}`).join(', ')
                : 'Cualquier tipo'}{' '}
              · {maximo === 1 ? 'un archivo' : `hasta ${maximo} archivos`}
            </span>
            <input
              {...comunes}
              accept={
                extensiones.length > 0 ? extensiones.map((e) => `.${e}`).join(',') : undefined
              }
              className="sr-only"
              disabled={!vivo}
              multiple={maximo > 1}
              onChange={(e) => p.cambiar?.([...(e.target.files ?? [])].map((f) => f.name))}
              type="file"
            />
          </label>
          {nombres.length > 0 && (
            <p className="mt-1.5 text-[13px] text-tinta-2">{nombres.join(', ')}</p>
          )}
          {pie}
        </div>
      )
    }

    case 'valoracion': {
      const minimo = numero(campo.minimo) ?? 1
      const maximo = Math.max(minimo, Math.min(numero(campo.maximo) ?? 5, minimo + 10))
      const actual = Number(p.valor)
      return (
        <div>
          <Grupo campo={campo} describe={describe}>
            <div className="flex flex-wrap gap-1">
              {Array.from({ length: maximo - minimo + 1 }, (_, i) => minimo + i).map((n) => (
                <button
                  aria-label={`${n} de ${maximo}`}
                  aria-pressed={vivo ? actual === n : undefined}
                  className="rounded-md p-0.5 transition-transform hover:scale-110"
                  key={n}
                  onClick={() => p.cambiar?.(n)}
                  tabIndex={vivo ? undefined : -1}
                  type="button"
                >
                  <Star
                    aria-hidden
                    className={clases(
                      'size-7',
                      vivo && actual >= n ? 'fill-(--marca) text-(--marca)' : 'text-linea-fuerte',
                    )}
                  />
                </button>
              ))}
            </div>
          </Grupo>
          {pie}
        </div>
      )
    }

    case 'escala': {
      const minimo = numero(campo.minimo) ?? 0
      const maximo = Math.max(minimo + 1, Math.min(numero(campo.maximo) ?? 10, minimo + 20))
      const actual =
        p.valor === undefined || p.valor === null || p.valor === '' ? null : Number(p.valor)
      return (
        <div>
          <Grupo campo={campo} describe={describe}>
            <div className="grid auto-cols-fr grid-flow-col gap-1">
              {Array.from({ length: maximo - minimo + 1 }, (_, i) => minimo + i).map((n) => (
                <button
                  aria-pressed={vivo ? actual === n : undefined}
                  className={clases(
                    'h-10 min-w-0 rounded-md border text-sm font-medium tabular-nums transition-colors',
                    vivo && actual === n
                      ? 'border-(--marca) bg-(--marca) text-(--texto-marca)'
                      : 'border-linea-fuerte bg-papel text-tinta hover:border-(--marca)',
                  )}
                  key={n}
                  onClick={() => p.cambiar?.(n)}
                  tabIndex={vivo ? undefined : -1}
                  type="button"
                >
                  {n}
                </button>
              ))}
            </div>
            {(texto(campo.etiquetaMinima) || texto(campo.etiquetaMaxima)) && (
              <div className="mt-1.5 flex justify-between gap-4 text-xs text-tenue">
                <span>{texto(campo.etiquetaMinima)}</span>
                <span className="text-right">{texto(campo.etiquetaMaxima)}</span>
              </div>
            )}
          </Grupo>
          {pie}
        </div>
      )
    }

    case 'calculado': {
      const total = p.totales?.[campo.clave]
      return (
        <div>
          <p className="mb-1.5 text-sm font-medium text-tinta">
            {campo.etiqueta || <span className="text-tenue">Sin etiqueta</span>}
          </p>
          <output
            className="flex items-center gap-2 rounded-lg border border-linea bg-papel-2 px-3 py-2.5 text-[15px] tabular-nums"
            id={id}
          >
            <Calculator aria-hidden className="size-4 shrink-0 text-tenue" />
            {vivo ? (
              typeof total === 'number' ? (
                total.toLocaleString('es')
              ) : (
                <span className="text-tenue">Se calcula al rellenar</span>
              )
            ) : (
              <span className="truncate font-mono text-[13px] text-tinta-2">
                {texto(campo.formula)
                  ? `= ${texto(campo.formula)}`
                  : 'Escribe la fórmula en los ajustes del campo'}
              </span>
            )}
          </output>
          {pie}
        </div>
      )
    }

    case 'pago': {
      const fijo = campo.modoDeImporte !== 'campo'
      const importe = vivo
        ? importeDelPago(campo, p.totales ?? {})
        : fijo
          ? (numero(campo.importe) ?? null)
          : null
      const desde = texto(campo.campoDeImporte)
      return (
        <div className="rounded-lg border border-(--marca)/30 bg-(--marca)/5 px-4 py-3.5">
          <div className="flex items-start gap-3">
            <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-papel text-(--marca) shadow-suave">
              <CreditCard aria-hidden className="size-4" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-tinta">{campo.etiqueta || 'Pago'}</p>
              <p className="font-display text-xl font-semibold tabular-nums">
                {importe !== null && importe > 0 ? (
                  formatear(aCentimos(importe, p.moneda), p.moneda)
                ) : fijo ? (
                  <span className="text-base font-normal text-tenue">
                    Escribe el importe en los ajustes del campo
                  </span>
                ) : (
                  <span className="text-base font-normal text-tenue">
                    {vivo
                      ? 'Se calcula al rellenar'
                      : `Lo que diga «${desde ? (p.etiquetaDe?.(desde) ?? desde) : '…'}»`}
                  </span>
                )}
              </p>
              <p className="mt-0.5 text-xs text-tenue">
                Se paga al enviar, con la pasarela de pago de tu web.
              </p>
            </div>
          </div>
          {pie}
        </div>
      )
    }

    case 'repetidor':
      return <Repetidor {...p} describe={describe} id={id} pie={pie} />

    case 'lugar':
      return vivo ? (
        <LugarEnVivo
          campo={campo}
          cambiar={p.cambiar}
          describe={describe}
          error={error}
          id={id}
          pie={pie}
          sitio={p.sitio}
        />
      ) : (
        <div>
          <Rotulo campo={campo} htmlFor={id} />
          <div className="grid gap-2 @md:grid-cols-2">
            {['País', 'Región, ciudad…'].map((t) => (
              <span className="relative block" key={t}>
                <select
                  className={clases(claseDeEntrada(error), 'appearance-none pr-9')}
                  id={t === 'País' ? id : undefined}
                  tabIndex={-1}
                >
                  <option>{t}</option>
                </select>
                <ChevronDown
                  aria-hidden
                  className="pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2 text-tenue"
                />
              </span>
            ))}
          </div>
          {pie}
        </div>
      )

    default: {
      // Los de una línea, y lo que el panel no conoce: un campo de texto con su etiqueta.
      const tipo = TIPO_DE_ENTRADA[campo.tipo] ?? 'text'
      return (
        <div>
          <Rotulo campo={campo} htmlFor={id} />
          <input
            {...comunes}
            className={claseDeEntrada(error)}
            inputMode={campo.tipo === 'telefono' ? 'tel' : undefined}
            max={campo.tipo === 'numero' ? (numero(campo.maximo) ?? undefined) : undefined}
            min={campo.tipo === 'numero' ? (numero(campo.minimo) ?? undefined) : undefined}
            onChange={(e) => p.cambiar?.(e.target.value)}
            placeholder={marcador}
            step={campo.tipo === 'numero' ? 'any' : undefined}
            type={tipo}
            value={vivo ? texto(p.valor) : ''}
          />
          {pie}
        </div>
      )
    }
  }
}

// --- El repetidor -------------------------------------------------------------------------

const Repetidor = (p: Props & { describe?: string; pie: ReactNode }) => {
  const { campo, modo } = p
  const vivo = modo === 'vivo'
  const subcampos = (Array.isArray(campo.campos) ? campo.campos : []) as CampoDePrevia[]
  const filas =
    Array.isArray(p.valor) && p.valor.length > 0
      ? (p.valor as Array<Record<string, unknown>>)
      : [{}]
  const maximo = numero(campo.maximoDeFilas) ?? 10
  const poner = (siguientes: Array<Record<string, unknown>>) => p.cambiar?.(siguientes)
  return (
    <div>
      <Grupo campo={campo} describe={p.describe}>
        <div className="flex flex-col gap-2">
          {filas.map((fila, i) => (
            <div
              className="flex items-end gap-2 rounded-lg border border-linea bg-papel-2/50 p-3"
              key={i}
            >
              <div className="grid min-w-0 flex-1 gap-2 @md:grid-flow-col @md:auto-cols-fr">
                {subcampos.map((sub) => {
                  const idSub = `${p.id}-${i}-${sub.clave || 'sub'}`
                  const errorSub = p.errores?.[`${campo.clave}.${i}.${sub.clave}`]
                  return (
                    <div className="min-w-0" key={sub.clave || sub.etiqueta}>
                      <label
                        className="mb-1 block text-[13px] font-medium text-tinta-2"
                        htmlFor={idSub}
                      >
                        {sub.etiqueta}
                        <Asterisco obligatorio={sub.obligatorio} />
                      </label>
                      <input
                        aria-describedby={errorSub ? `${idSub}-error` : undefined}
                        aria-invalid={errorSub ? true : undefined}
                        className={claseDeEntrada(errorSub)}
                        id={idSub}
                        onChange={(e) =>
                          poner(
                            filas.map((f, j) =>
                              j === i ? { ...f, [sub.clave]: e.target.value } : f,
                            ),
                          )
                        }
                        readOnly={!vivo}
                        tabIndex={vivo ? undefined : -1}
                        type={TIPO_DE_ENTRADA[sub.tipo] ?? 'text'}
                        value={vivo ? texto(fila[sub.clave]) : ''}
                      />
                      {errorSub && (
                        <p
                          className="mt-1 text-[13px] font-medium text-error"
                          id={`${idSub}-error`}
                          role="alert"
                        >
                          {errorSub}
                        </p>
                      )}
                    </div>
                  )
                })}
                {subcampos.length === 0 && (
                  <p className="text-sm text-tenue">
                    Elige en los ajustes del campo qué se repite.
                  </p>
                )}
              </div>
              {vivo && filas.length > 1 && (
                <button
                  aria-label={`Quitar la fila ${i + 1}`}
                  className="mb-1 rounded-md p-2 text-tenue hover:bg-papel-3 hover:text-error"
                  onClick={() => poner(filas.filter((_, j) => j !== i))}
                  type="button"
                >
                  <Trash2 aria-hidden className="size-4" />
                </button>
              )}
            </div>
          ))}
          {(!vivo || filas.length < maximo) && (
            <button
              className="inline-flex items-center gap-1.5 self-start rounded-lg px-2 py-1.5 text-sm font-medium text-(--marca) hover:bg-(--marca)/5"
              onClick={() => poner([...filas, {}])}
              tabIndex={vivo ? undefined : -1}
              type="button"
            >
              <Plus aria-hidden className="size-4" /> Añadir otra
            </button>
          )}
        </div>
      </Grupo>
      {p.pie}
    </div>
  )
}

// --- País y lugar ---------------------------------------------------------------------------

type Lugares = {
  lugares: Array<{ codigo: string; nombre: string; hoja?: boolean; ultimo?: boolean }>
  nombreDelNivel?: null | string
}

/** Los niveles que pide el campo: el primero, el segundo, o hasta el último que tenga el país. */
const NIVELES: Record<string, number> = { primero: 1, segundo: 2 }

const nombreDePais = (codigo: string): string => {
  try {
    return new Intl.DisplayNames(['es'], { type: 'region' }).of(codigo) ?? codigo
  } catch {
    return codigo
  }
}

/**
 * «País y lugar» en la vista previa: desplegables encadenados con los lugares oficiales que
 * da el núcleo (`/publico/lugares`, el mismo que usa el carrito de la web). Si la lista no se
 * puede cargar, se dice; el resto del formulario sigue funcionando.
 */
const LugarEnVivo = ({
  campo,
  sitio,
  id,
  cambiar,
  error,
  describe,
  pie,
}: {
  cambiar?: (valor: unknown) => void
  campo: CampoDePrevia
  describe?: string
  error?: string
  id: string
  pie: ReactNode
  sitio: number
}) => {
  const permitidos = Array.isArray(campo.paises)
    ? (campo.paises as unknown[]).map(String).filter(Boolean)
    : []
  const tope = NIVELES[texto(campo.nivel)] ?? Number.POSITIVE_INFINITY
  const [pais, setPais] = useState(permitidos.length === 1 ? permitidos[0]! : '')
  const [elegidos, setElegidos] = useState<Array<{ codigo: string; final: boolean }>>([])
  const { data, isError } = useQuery({
    queryKey: ['lugares-de-la-web', sitio],
    queryFn: () =>
      pedir<{ paises?: Array<{ codigo: string; nombre: string }> }>(
        `/publico/lugares${consulta({ sitio })}`,
      ),
    enabled: permitidos.length === 0,
    retry: false,
    staleTime: 5 * 60_000,
  })
  const paises =
    permitidos.length > 0
      ? permitidos.map((codigo) => ({ codigo, nombre: nombreDePais(codigo) }))
      : (data?.paises ?? [])

  const completo = (lista: Array<{ codigo: string; final: boolean }>) => {
    const ultimo = lista.at(-1)
    return Boolean(ultimo && (ultimo.final || lista.length >= tope))
  }
  const avisar = (p: string, lista: Array<{ codigo: string; final: boolean }>) =>
    cambiar?.(p && completo(lista) ? `${p}:${lista.at(-1)!.codigo}` : undefined)

  const niveles = completo(elegidos) ? elegidos.length : elegidos.length + 1
  return (
    <div>
      <Grupo campo={campo} describe={describe}>
        <div className="grid gap-2 @md:grid-cols-2">
          <span className="relative block">
            <label className="sr-only" htmlFor={id}>
              País
            </label>
            <select
              aria-invalid={error ? true : undefined}
              className={clases(claseDeEntrada(error), 'appearance-none pr-9')}
              id={id}
              onChange={(e) => {
                setPais(e.target.value)
                setElegidos([])
                avisar(e.target.value, [])
              }}
              value={pais}
            >
              <option value="">País</option>
              {paises.map((p) => (
                <option key={p.codigo} value={p.codigo}>
                  {p.nombre}
                </option>
              ))}
            </select>
            <ChevronDown
              aria-hidden
              className="pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2 text-tenue"
            />
          </span>
          {pais &&
            Array.from({ length: Math.min(niveles, tope) }, (_, k) => (
              <NivelDeLugar
                elegido={elegidos[k]?.codigo ?? ''}
                error={error}
                id={`${id}-nivel-${k}`}
                key={`${pais}-${k}-${elegidos[k - 1]?.codigo ?? ''}`}
                padre={k === 0 ? null : (elegidos[k - 1]?.codigo ?? null)}
                pais={pais}
                poner={(codigo, final) => {
                  const lista = [...elegidos.slice(0, k), ...(codigo ? [{ codigo, final }] : [])]
                  setElegidos(lista)
                  avisar(pais, lista)
                }}
                sitio={sitio}
              />
            ))}
        </div>
        {isError && permitidos.length === 0 && (
          <p className="mt-1.5 text-[13px] text-tenue">
            La lista de países no se ha podido cargar en la vista previa. En tu web sale la de
            verdad.
          </p>
        )}
      </Grupo>
      {pie}
    </div>
  )
}

const NivelDeLugar = ({
  sitio,
  pais,
  padre,
  elegido,
  poner,
  id,
  error,
}: {
  elegido: string
  error?: string
  id: string
  padre: null | string
  pais: string
  poner: (codigo: string, final: boolean) => void
  sitio: number
}) => {
  const { data, isError, isPending } = useQuery({
    queryKey: ['lugares-de-la-web', sitio, pais, padre],
    queryFn: () =>
      pedir<Lugares>(`/publico/lugares${consulta({ sitio, pais, ...(padre ? { padre } : {}) })}`),
    retry: false,
    staleTime: 10 * 60_000,
  })
  const nombre = data?.nombreDelNivel || 'Lugar'
  return (
    <span className="relative block">
      <label className="sr-only" htmlFor={id}>
        {nombre}
      </label>
      <select
        aria-invalid={error ? true : undefined}
        className={clases(claseDeEntrada(error), 'appearance-none pr-9')}
        disabled={isPending || isError}
        id={id}
        onChange={(e) => {
          const lugar = data?.lugares.find((l) => l.codigo === e.target.value)
          poner(e.target.value, Boolean(lugar?.hoja || lugar?.ultimo))
        }}
        value={elegido}
      >
        <option value="">
          {isPending ? 'Cargando…' : isError ? 'No se ha podido cargar' : nombre}
        </option>
        {(data?.lugares ?? []).map((l) => (
          <option key={l.codigo} value={l.codigo}>
            {l.nombre}
          </option>
        ))}
      </select>
      <ChevronDown
        aria-hidden
        className="pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2 text-tenue"
      />
    </span>
  )
}
